import { useState } from 'react';
import { ImageIcon, ImageOff } from 'lucide-react';
import { useSignedUrlState } from '@/hooks/useSignedUrl';
import { Button } from '@/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

interface InspectionPhotoProps {
    /** The stored, public-style URL in the private `vehicle-inspection-photos` bucket. */
    url: string;
    /** What the photo shows: an angle ("Front 3/4") or a line ("Tyres, including the spare"). */
    label: string;
    className?: string;
}

/**
 * One inspection photo: a thumbnail signed through `useSignedUrl`, and a tap that opens it full
 * size. Tap, not hover: a partner on a phone has no hover, and the zoom is the point of the photo.
 *
 * A photo that will not sign, or whose signed address stops loading (one that expired on a page
 * left open), says so and takes a tap to sign it again: on a Held card the photo is the evidence,
 * so "still loading" and "missing" must not look alike.
 */
export function InspectionPhoto({ url, label, className }: InspectionPhotoProps) {
    const { url: signedUrl, failed: signFailed, retry } = useSignedUrlState(url);
    const [loadFailed, setLoadFailed] = useState(false);

    if (signFailed || loadFailed) {
        return (
            <button
                type="button"
                onClick={() => { setLoadFailed(false); retry(); }}
                className={cn(
                    'flex aspect-square flex-col items-center justify-center gap-1 overflow-hidden rounded-md border bg-muted px-1 text-center text-xs leading-tight text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                    className,
                )}
                aria-label={`${label}: photo unavailable. Try again`}
            >
                <ImageOff className="h-5 w-5 shrink-0" aria-hidden="true" />
                <span>Photo unavailable</span>
                <span className="font-medium text-foreground underline underline-offset-2">Try again</span>
            </button>
        );
    }

    if (!signedUrl) {
        return (
            <div
                className={cn('aspect-square overflow-hidden rounded-md border', className)}
                role="img"
                aria-label={`${label}: loading photo`}
            >
                <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                    <ImageIcon className="h-5 w-5" aria-hidden="true" />
                </div>
            </div>
        );
    }

    return (
        <Dialog>
            <DialogTrigger asChild>
                <button
                    type="button"
                    className={cn(
                        'aspect-square overflow-hidden rounded-md border bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                        className,
                    )}
                    aria-label={`Open photo: ${label}`}
                >
                    <img
                        src={signedUrl}
                        alt={label}
                        loading="lazy"
                        className="h-full w-full object-cover"
                        onError={() => setLoadFailed(true)}
                    />
                </button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl gap-3 p-3 sm:p-4" aria-describedby={undefined}>
                <DialogTitle className="pr-8 text-sm font-medium">{label}</DialogTitle>
                <img src={signedUrl} alt={label} className="max-h-[70vh] w-full rounded-md object-contain" />
                {/* The stock corner X is a 16px target over a full-width photo; this one takes a thumb. */}
                <DialogClose asChild>
                    <Button type="button" variant="outline" className="h-11 w-full">Close</Button>
                </DialogClose>
            </DialogContent>
        </Dialog>
    );
}
